// web/src/lib/compressImage.js
// Client-side image compression for photo uploads.
// Targets <= 2MB and <= 2048px on the long edge, re-encoded as JPEG.
// Handles HEIC/HEIF (iPhone) by converting to JPEG first, since most
// browsers can't decode HEIC to a canvas.
//
// Deps:  npm i browser-image-compression heic2any   (run in web\)
// heic2any is dynamically imported so its WASM only loads when a HEIC is picked.

import imageCompression from 'browser-image-compression';

const HEIC_TYPES = ['image/heic', 'image/heif'];

// Accepted INPUT types for any image picker in the app. HEIC/HEIF are included
// so iPhone photos aren't filtered out of the picker — they're converted to
// JPEG client-side before upload, because the Worker only stores jpeg/png/
// webp/gif (see api/src/lib/storage.ts:IMAGE_EXT).
export const IMAGE_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif';

const TYPE_OK = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif',
]);
// Some browsers report an empty MIME type for .heic — fall back to extension.
const EXT_OK = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

// Sanity cap on the ORIGINAL, to catch a video or a huge raw export before we
// spend time decoding it. Compression takes the result far below this.
export const MAX_INPUT_BYTES = 50 * 1024 * 1024; // 50 MB
// The Worker's hard limit. The compressed JPEG lands ~2 MB, well under.
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024; // 8 MB

export const isGif = (f) => f.type === 'image/gif' || /\.gif$/i.test(f.name);

export function isAcceptableImage(f) {
  return TYPE_OK.has(f.type) || (f.type === '' && EXT_OK.test(f.name)) || EXT_OK.test(f.name);
}

function isHeic(file) {
  return HEIC_TYPES.includes(file.type) || /\.hei[cf]$/i.test(file.name);
}

/**
 * Validate and compress one picked file into something the Worker will accept.
 * This is the single entry point every upload path should use — going straight
 * to api.upload() ships whatever the camera produced, which on an iPhone is a
 * multi-megabyte HEIC the server rejects outright with 415.
 *
 * Throws an Error whose `.code` is one of: unsupported | input_too_large |
 * file_too_large. GIFs pass through uncompressed so animation survives.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function prepareImageUpload(file, opts = {}) {
  if (!isAcceptableImage(file)) throw withCode('unsupported');
  if (file.size > MAX_INPUT_BYTES) throw withCode('input_too_large');

  const out = isGif(file)
    ? file
    : await compressImage(file, { maxSizeMB: 2, maxWidthOrHeight: 2048, ...opts });

  if (out.size > MAX_UPLOAD_BYTES) throw withCode('file_too_large');
  return out;
}

function withCode(code) {
  const e = new Error(code);
  e.code = code;
  return e;
}

function withJpgName(name) {
  return name.replace(/\.[^.]+$/, '') + '.jpg';
}

/**
 * Compress a single image File. Returns a new JPEG File.
 * Non-image files are returned untouched (caller can still reject them).
 *
 * @param {File} file
 * @param {{ maxSizeMB?: number, maxWidthOrHeight?: number, initialQuality?: number }} opts
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  const {
    maxSizeMB = 2,
    maxWidthOrHeight = 2048,
    initialQuality = 0.8,
  } = opts;

  // Only process images.
  if (!file.type.startsWith('image/') && !isHeic(file)) {
    return file;
  }

  let working = file;

  // 1) HEIC/HEIF -> JPEG (browser-image-compression can't decode HEIC itself).
  if (isHeic(file)) {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    working = new File([blob], withJpgName(file.name), { type: 'image/jpeg' });
  }

  // 2) Resize + compress to hit the size target.
  const compressed = await imageCompression(working, {
    maxSizeMB,
    maxWidthOrHeight,
    initialQuality,
    useWebWorker: true,
    fileType: 'image/jpeg',
    preserveExif: false, // strips EXIF -> smaller files + drops GPS location from barn photos.
                         // Orientation is baked into pixels by the library before stripping.
  });

  return new File([compressed], withJpgName(working.name), { type: 'image/jpeg' });
}

/**
 * Compress many files. Runs sequentially to avoid spiking phone memory
 * when someone multi-selects a dozen full-res photos.
 * @param {File[]} files
 * @param {object} opts
 * @returns {Promise<File[]>}
 */
export async function compressImages(files, opts = {}) {
  const out = [];
  for (const f of files) {
    out.push(await compressImage(f, opts));
  }
  return out;
}
